/**
 * RettVerk Landing Page
 * Synkronisert med App DNA
 * 
 * Fargepalett:
 * - Bakgrunn: var(--bg-deep) #0B1120
 * - Overflate: var(--bg-surface) #131C2E
 * - Aksent: var(--copper) #C07850
 * - Tekst: var(--text-main) #F0F4F8
 */

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowRight, Shield, CheckCircle, Info,
  Users, Scale, Briefcase, Building2, Home, Map, ScrollText, Heart,
  FileText, Clock, Calendar
} from 'lucide-react';
import { usePageSEO } from '../../../components/SEO';
import { LandingHeader } from '../../../components/shared/navigation/LandingHeader';

import {
  CONFIG,
  MENU_ITEMS,
  LAW_MODULES,
  InfoModal,
} from '../index';

// ============================================================
// ICON MAP
// ============================================================
const ICON_MAP = {
  Users, Scale, Briefcase, Building2, Home, Map, ScrollText, Heart
};

// ============================================================
// TIMELINE DATA
// ============================================================
// PROCESS STEPS DATA - Arbeidsrett Oppsigelsesprosess
// ============================================================
const PROCESS_STEPS = [
  { 
    id: 1, 
    label: 'Dokument mottatt', 
    description: 'Oppsigelsen lagres lokalt og kontrolleres for formkrav etter arbeidsmiljøloven.',
    icon: FileText 
  },
  { 
    id: 2, 
    label: 'AI-drevet analyse', 
    description: 'Systemet identifiserer saklighetsmomenter, mangler, frister og relevante lovhenvisninger.',
    icon: Scale 
  },
  { 
    id: 3, 
    label: 'Automatisk fristberegning', 
    description: 'Oppsigelsestid, klagefrister og øvrige tidsløp beregnes basert på kontrakt og lovverk.',
    icon: Calendar
  },
  { 
    id: 4, 
    label: 'Dokumentert prosess', 
    description: 'Det genereres et strukturert notat eller klageutkast som brukeren selv kvalitetssikrer.',
    icon: CheckCircle 
  }
];

// ============================================================
// PROCESS EXAMPLE COMPONENT - Master Grade
// ============================================================
function ProcessExample() {
  const [visible, setVisible] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) setVisible(true); },
      { threshold: 0.2 }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);

  return (
    <div ref={ref} className="w-full">
      <div className="space-y-3">
        {PROCESS_STEPS.map((step, i) => {
          const Icon = step.icon;
          const isVisible = visible;
          
          return (
            <div 
              key={step.id}
              className={`
                flex gap-4 p-3 rounded-lg
                bg-[var(--bg-highlight)]/20 border border-[var(--border-subtle)]/50
                transition-all duration-500 ease-out
                ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-4'}
              `}
              style={{ transitionDelay: `${i * 150 + 200}ms` }}
            >
              {/* Step number + icon */}
              <div className="flex-shrink-0 flex items-start gap-2">
                <span className="w-6 h-6 rounded-full bg-[var(--copper)]/20 text-[var(--copper)] flex items-center justify-center text-xs font-semibold">
                  {step.id}
                </span>
                <Icon className="w-4 h-4 text-[var(--copper)] mt-1" />
              </div>
              
              {/* Content */}
              <div className="flex-1 min-w-0">
                <h4 className="text-sm font-medium text-[var(--text-main)] mb-0.5">
                  {step.label}
                </h4>
                <p className="text-xs text-[var(--text-muted)] leading-relaxed">
                  {step.description}
                </p>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ============================================================
// TRUST SEAL - App Style med glow-effekt
// ============================================================
function TrustSeal() {
  return (
    <div className="inline-flex items-center gap-3 px-4 py-2.5 bg-[var(--success)]/10 border border-[var(--success)]/30 rounded-lg ring-2 ring-[var(--success)]/20 hover:ring-[var(--success)]/40 transition-all duration-300">
      <Shield className="w-5 h-5 text-[var(--success)]" />
      <div>
        <span className="block text-xs font-semibold text-[var(--success)]">AML § 15-4</span>
        <span className="block text-[10px] text-[var(--text-muted)]">Lovlighetskontrollert</span>
      </div>
    </div>
  );
}

// ============================================================
// MODULE CARD - Kompakt versjon for 3x3 grid
// ============================================================
function ModuleCard({ module, index, show, onClick }) {
  const IconComponent = ICON_MAP[module.icon] || Scale;
  const isActive = module.status === 'active';
  const isMaster = module.badge === 'Master';
  
  const handleClick = () => {
    onClick(module.id);
  };

  return (
    <button
      onClick={handleClick}
      data-testid={`module-card-${module.id}`}
      className={`
        group relative text-left p-4 rounded-xl
        bg-[var(--bg-surface)] border border-[var(--border-subtle)]
        hover:border-[var(--copper)]/50 hover:bg-[var(--bg-highlight)]
        hover:scale-[1.02]
        hover:shadow-lg hover:shadow-[var(--copper)]/10
        transition-all duration-300 ease-out
        ${show ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}
        ${!isActive && 'opacity-70'}
      `}
      style={{ transitionDelay: `${index * 50 + 300}ms` }}
    >
      {/* Master badge - kun for aktive */}
      {isMaster && (
        <span className="absolute -top-2 right-3 px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider bg-[var(--copper)] text-white rounded">
          Master
        </span>
      )}

      {/* Header med ikon og tittel */}
      <div className="flex items-start gap-3 mb-2">
        {/* Icon */}
        <div className={`
          w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0
          ${isActive 
            ? 'bg-[var(--copper)]/20' 
            : 'bg-[var(--bg-highlight)]'
          }
        `}>
          <IconComponent className={`w-4 h-4 ${
            isActive ? 'text-[var(--copper)]' : 'text-[var(--text-muted)]'
          }`} />
        </div>
        
        {/* Title with status indicator */}
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-semibold text-[var(--text-main)] flex items-center gap-1.5">
            {module.title}
            {isActive ? (
              <span 
                className="w-2 h-2 rounded-full bg-[var(--success)] shadow-[0_0_6px_1px_rgba(34,197,94,0.5)]"
                title="Aktiv modul"
              />
            ) : (
              <span 
                className="w-2 h-2 rounded-full bg-red-500 shadow-[0_0_6px_1px_rgba(239,68,68,0.4)]"
                title="Kommer snart"
              />
            )}
          </h3>
          {!isActive && (
            <span className="text-[10px] text-red-400/80 uppercase tracking-wide">
              Kommer snart
            </span>
          )}
        </div>
      </div>
      
      {/* Description - kortere */}
      <p className="text-xs text-[var(--text-muted)] leading-relaxed line-clamp-2">
        {module.shortSell || module.description}
      </p>

      {/* Info icon */}
      <Info className="absolute bottom-3 right-3 w-3.5 h-3.5 text-[var(--text-muted)]/40 group-hover:text-[var(--copper)] transition-all" />
    </button>
  );
}

// ============================================================
// LANDING PAGE - APP DNA SYNCHRONIZED
// ============================================================
export default function LandingPage() {
  const navigate = useNavigate();
  const [activeModal, setActiveModal] = useState(null);
  const [show, setShow] = useState(false);

  // Apply SEO meta tags for landing page
  usePageSEO('home');
  
  // Analytics - logg sidebesøk
  useEffect(() => {
    const logVisit = async () => {
      try {
        const sessionKey = 'rettverk_visit_home';
        if (!sessionStorage.getItem(sessionKey)) {
          await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/analytics/visit`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ page: '/', referrer: document.referrer || null })
          });
          sessionStorage.setItem(sessionKey, 'true');
        }
      } catch (e) { /* silent fail */ }
    };
    const timeout = setTimeout(logVisit, 2000);
    return () => clearTimeout(timeout);
  }, []);

  useEffect(() => {
    setTimeout(() => setShow(true), 100);
  }, []);

  return (
    <div 
      className="min-h-screen bg-[var(--bg-deep)]" 
      data-testid="landing-page"
    >
      {/* Subtle gradient overlay */}
      <div className="fixed inset-0 pointer-events-none">
        <div 
          className="absolute inset-0"
          style={{ 
            background: 'linear-gradient(180deg, var(--bg-deep) 0%, #070B14 100%)'
          }} 
        />
      </div>

      {/* Container */}
      <div className="relative z-10 max-w-[1440px] mx-auto px-6 lg:px-12">
        
        {/* ============================================ */}
        {/* HEADER - Shared component */}
        {/* ============================================ */}
        <LandingHeader show={show} />

        {/* ============================================ */}
        {/* HERO SECTION - Kompakt */}
        {/* ============================================ */}
        <section className="pt-8 lg:pt-12 pb-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            
            {/* Left Column - Text */}
            <div>
              {/* H1 */}
              <h1 
                className={`
                  text-3xl lg:text-4xl xl:text-5xl font-bold leading-tight mb-2
                  text-[var(--text-main)]
                  transition-all duration-500
                  ${show ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}
                `}
                style={{ fontFamily: "'Playfair Display', Georgia, serif", transitionDelay: '100ms' }}
              >
                Din rettssikkerhet.
                <br />
                <span className="text-[var(--copper)]">I dine hender.</span>
              </h1>
              
              {/* Slogan - under H1, over brødtekst */}
              <p 
                className={`
                  text-[var(--copper)] text-sm italic mb-6
                  transition-all duration-500
                  ${show ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}
                `}
                style={{ transitionDelay: '150ms' }}
              >
                Klare svar · Trygge dokumenter · Full kontroll
              </p>
              
              {/* Mission text */}
              <p 
                className={`
                  text-base text-[var(--text-muted)] max-w-lg mb-5 leading-relaxed
                  transition-all duration-500
                  ${show ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}
                `}
                style={{ transitionDelay: '200ms' }}
              >
                RettVerk utjevner maktbalansen. Ved hjelp av AI og lokal lagring 
                får du juridisk kontroll — <span className="text-[var(--text-main)]">helt privat og i tråd med norsk lov.</span>
              </p>
              
              {/* CTA + Seal */}
              <div 
                className={`
                  flex flex-wrap items-center gap-3
                  transition-all duration-500
                  ${show ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}
                `}
                style={{ transitionDelay: '300ms' }}
              >
                {/* Primary CTA */}
                <button
                  onClick={() => navigate('/auth')}
                  data-testid="cta-hero-btn"
                  className="
                    inline-flex items-center gap-2 px-5 py-2.5
                    bg-[var(--copper)] hover:bg-[var(--copper-hover)]
                    text-white text-sm font-semibold
                    rounded-lg transition-all duration-300
                    hover:gap-2.5 hover:shadow-lg hover:shadow-[var(--copper)]/25
                    hover:scale-[1.02] active:scale-[0.98]
                  "
                >
                  <span>Start gratis</span>
                  <ArrowRight className="w-4 h-4" />
                </button>
                
                {/* Trust Seal */}
                <TrustSeal />
              </div>
            </div>

            {/* Right Column - Process Example */}
            <div 
              className={`
                transition-all duration-500 hidden lg:block
                ${show ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}
              `}
              style={{ transitionDelay: '400ms' }}
            >
              <div className="bg-[var(--bg-surface)]/80 backdrop-blur-sm border border-[var(--border-subtle)] rounded-xl p-5 hover:border-[var(--copper)]/30 transition-colors duration-300">
                <div className="flex items-center gap-2 mb-4">
                  <Briefcase className="w-4 h-4 text-[var(--copper)]" />
                  <span className="text-xs text-[var(--text-main)] uppercase tracking-wider font-medium">
                    Eksempel: Oppsigelsesprosess
                  </span>
                </div>
                <ProcessExample />
              </div>
            </div>
          </div>
        </section>

        {/* ============================================ */}
        {/* MODULE GRID - 3x3 */}
        {/* ============================================ */}
        <section className="pb-8">
          {/* Section header */}
          <div 
            className={`
              flex items-center gap-4 mb-6
              transition-all duration-500
              ${show ? 'opacity-100' : 'opacity-0'}
            `}
            style={{ transitionDelay: '500ms' }}
          >
            <div className="flex-1 h-px bg-[var(--border-subtle)]" />
            <span className="text-xs text-[var(--text-muted)] uppercase tracking-wider font-medium">
              Våre rettsområder
            </span>
            <div className="flex-1 h-px bg-[var(--border-subtle)]" />
          </div>
          
          {/* 3x3 modules grid - alle moduler i samme størrelse */}
          <div className="grid grid-cols-3 gap-3" data-testid="law-modules-grid">
            {LAW_MODULES.map((module, i) => (
              <ModuleCard 
                key={module.id}
                module={module}
                index={i}
                show={show}
                onClick={(id) => setActiveModal(id)}
              />
            ))}
          </div>
        </section>

        {/* ============================================ */}
        {/* FOOTER - Kompakt */}
        {/* ============================================ */}
        <footer 
          className={`
            border-t border-[var(--border-subtle)] py-4
            transition-all duration-500
            ${show ? 'opacity-100' : 'opacity-0'}
          `}
          style={{ transitionDelay: '600ms' }}
        >
          <div className="flex flex-wrap justify-center gap-4 mb-2">
            {MENU_ITEMS.map((item) => (
              <button
                key={item.id}
                onClick={() => setActiveModal(item.id)}
                className="text-xs text-[var(--text-muted)] hover:text-[var(--copper)] transition-colors"
              >
                {item.title || item.label}
              </button>
            ))}
          </div>
          
          <p className="text-center text-[10px] text-[var(--text-muted)]/60">
            © 2026 RettVerk. Norsk juridisk assistent.
          </p>
        </footer>
      </div>

      {/* Modal */}
      <InfoModal 
        isOpen={activeModal !== null} 
        onClose={() => setActiveModal(null)} 
        modalId={activeModal} 
      />
    </div>
  );
}
